import { useMutation } from '@tanstack/react-query';
import {
  API_STATUS_FAILED,
  CODE_TWO_FACTOR_FAILED,
} from '@/constants/error-codes';
import { legacyRestFetch } from '@/features/account-settings/legacyRestFetch';
import type { Security2FATypeEnabled } from '@/shared/types/auth';
import { getDeviceUUID } from '@/shared/utils/cookieUtils';

export function useVerifyTwoFactorCode() {
  const { mutateAsync: verifyCode } = useMutation({
    mutationFn: async ({
      code,
      twoFactorType,
    }: {
      code: string;
      twoFactorType: Security2FATypeEnabled;
    }) => {
      const body = new FormData();
      body.append('token', code);
      body.append('device_uuid', getDeviceUUID());
      body.append('two_factor_type', twoFactorType);
      body.append('verify_is_allowed', '1');

      // TODO(OTWEB-9323): Migrate to api-client
      const response = await legacyRestFetch('verify_otp_token', {
        method: 'POST',
        body,
      });

      if (response.status !== 200 && response.status !== 400) {
        throw new TwoFactoryAuthCodeVerificationError('Server error');
      }

      const data = await response.json();

      if (
        data.status === API_STATUS_FAILED &&
        data.code === CODE_TWO_FACTOR_FAILED
      ) {
        return {
          isVerified: false,
          delayBeforeRetryingMs: data.delay_before_retrying_ms ?? 0,
        };
      }

      if (data.status === API_STATUS_FAILED) {
        throw new TwoFactoryAuthCodeVerificationError(data.message);
      }

      return {
        isVerified: true,
        delayBeforeRetryingMs: data.delay_before_retrying_ms ?? 0,
      };
    },
  });

  return { verifyCode };
}

class TwoFactoryAuthCodeVerificationError extends Error {
  name = 'TwoFactoryAuthCodeVerificationError';
  constructor(message: string) {
    super(`Two-factor authentication code verification error: ${message}`);
  }
}
